"use client";

import {
	PanelResizer,
	useLeftPanelWidth,
} from "@saas/ai-image-generator/components/PanelResizer";
import { SECTION_TITLE } from "@shared/constants/typography";
import { FileText } from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

export function PromptToolWorkbench({
	title,
	description,
	children,
	action,
	result,
	resultTitle,
	emptyLabel,
}: {
	title: string;
	description: ReactNode;
	children: ReactNode;
	action: ReactNode;
	result: ReactNode;
	resultTitle: string;
	emptyLabel: string;
}) {
	const t = useTranslations("aiImageGenerator.client");
	const { width, commit } = useLeftPanelWidth();

	return (
		<div
			id="prompt-tool-workbench"
			className="studio-theme w-full py-2 sm:px-4 studio-split:-mx-8 studio-split:-mt-8 studio-split:w-auto studio-split:p-0"
		>
			<div className="flex flex-col studio-split:h-[calc(100dvh-var(--language-banner-height,0px))] studio-split:min-h-[560px] studio-split:flex-row studio-split:overflow-hidden">
				<section className="flex min-h-0 w-full shrink-0 flex-col bg-background studio-split:max-w-[calc(100%-280px)] studio-split:w-[var(--left-panel-w,412px)]">
					<div className="min-h-0 flex-1 space-y-7 pb-6 studio-split:overflow-y-auto studio-split:p-4">
						<header>
							<h1
								className={`${SECTION_TITLE.className} !text-left !text-2xl`}
							>
								{title}
							</h1>
							<div className="mt-3 text-sm leading-relaxed text-muted-foreground">
								{description}
							</div>
						</header>
						{children}
					</div>
					<div className="h-20 sm:hidden" aria-hidden />
					<div className="z-30 shrink-0 bg-background py-3 max-sm:fixed max-sm:inset-x-0 max-sm:bottom-0 max-sm:border-t max-sm:px-4 max-sm:pb-[max(12px,env(safe-area-inset-bottom))] studio-split:p-4 [&>button]:h-12 [&>button]:w-full [&>button]:rounded-full">
						{action}
					</div>
				</section>
				<PanelResizer
					width={width}
					onCommit={commit}
					label={t("resizePanels")}
				/>
				<section className="min-h-[360px] min-w-0 flex-1 bg-zinc-100 p-4 dark:bg-muted/30 sm:p-6 studio-split:overflow-y-auto">
					{!result && (
						<h2 className="mb-5 text-sm font-medium text-muted-foreground">
							{resultTitle}
						</h2>
					)}
					{result || (
						<div className="flex min-h-[320px] flex-col items-center justify-center gap-4 rounded-2xl bg-background p-8 text-center text-muted-foreground">
							<FileText
								className="size-10 opacity-40"
								aria-hidden
							/>
							<p className="text-sm">{emptyLabel}</p>
						</div>
					)}
				</section>
			</div>
		</div>
	);
}
